import { api } from './http';
import type {
  CsvFile,
  CsvFileUploadResult,
  CsvFilesFilters,
  CsvFilesResponse,
  CsvFileVersionsResponse,
  CsvFileHeadersResponse,
  CsvFileValidationStatus,
  CsvFileVersion,
  CsvFileVersionApi,
} from '@/types/csvFiles';
import { API_ENDPOINTS } from '@/config/constants';
import type { User } from '@/types/auth';

/**
 * Raw CSV file response from API (before normalization)
 * This type represents the actual structure returned by the backend API
 */
interface CsvFileApiResponse {
  id: number;
  study_id: number;
  filename: string;
  size: number;
  content_type: string;
  file_url: string;
  created_at: string;
  updated_at: string;
  user: User;
  last_version_id: number;
  // Optional nested version objects from API
  last_version?: CsvFileVersionApi;
  latest_imported_version?: CsvFileVersionApi | null;
  version_for_import?: CsvFileVersionApi | null;
}

function normalizeCsvFileFromApi(input: CsvFileApiResponse): CsvFile {
  const file = input;

  const lastVersion = file.last_version;
  const latestImportedVersion = file.latest_imported_version;
  const versionForImport = file.version_for_import;

  const normalized: CsvFile = {
    ...file,
    last_version: lastVersion,
    latest_imported_version:
      latestImportedVersion === undefined ? undefined : latestImportedVersion,
    version_for_import:
      versionForImport === undefined ? undefined : versionForImport,
  };

  // Derive list status + version number from last_version.
  if (lastVersion) {
    normalized.validation_status = lastVersion.status;
    normalized.version_number = lastVersion.order_index;
  }

  // Derive imported info from latest_imported_version for existing UI components.
  if (latestImportedVersion) {
    normalized.last_imported_version = latestImportedVersion.order_index;
    normalized.last_imported_at = latestImportedVersion.imported_at || undefined;
    normalized.last_imported_by = latestImportedVersion.imported_by || undefined;
  }

  return normalized;
}

// CSV Files API functions
export async function getCsvFiles(
  filters?: CsvFilesFilters
): Promise<CsvFilesResponse> {
  const params = new URLSearchParams();

  if (filters?.page) {
    params.append('page', filters.page.toString());
  }

  if (filters?.per_page) {
    params.append('per_page', filters.per_page.toString());
  }

  if (filters?.order) {
    params.append('order', filters.order);
  }

  if (filters?.order_direction) {
    params.append('order_direction', filters.order_direction);
  }

  if (filters?.ready_for_import === true) {
    params.append('filter[ready_for_import]', 'true');
  }

  const url = `${API_ENDPOINTS.CSV_FILES}${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await api.get(url);

  // Extract data from the response structure
  if (
    response.data &&
    response.data.data &&
    Array.isArray(response.data.data) &&
    response.data.pagination
  ) {
    return {
      data: (response.data.data as CsvFileApiResponse[]).map(normalizeCsvFileFromApi),
      pagination: response.data.pagination,
    };
  }

  // Fallback for direct array response
  if (Array.isArray(response.data)) {
    return {
      data: (response.data as CsvFileApiResponse[]).map(normalizeCsvFileFromApi),
      pagination: {
        current_page: 1,
        next_page: null,
        prev_page: null,
        total_pages: 1,
        total_count: response.data.length,
        per_page: response.data.length,
      },
    };
  }
  return {
    data: [],
    pagination: {
      current_page: 1,
      next_page: null,
      prev_page: null,
      total_pages: 1,
      total_count: 0,
      per_page: 10,
    },
  };
}

// Upload CSV file directly (without upload queue)
export async function uploadCsvFile(
  file: File,
  options?: { signal?: AbortSignal }
): Promise<CsvFileUploadResult> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(API_ENDPOINTS.CSV_FILES, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    signal: options?.signal,
  });

  return response.data;
}

// Get single CSV file
export async function getCsvFile(id: number): Promise<CsvFile> {
  const response = await api.get(`${API_ENDPOINTS.CSV_FILES}/${id}`);

  if (response.data && response.data.data) {
    return normalizeCsvFileFromApi(response.data.data as CsvFileApiResponse);
  }

  return normalizeCsvFileFromApi(response.data as CsvFileApiResponse);
}

// Delete CSV file
export async function deleteCsvFile(id: number): Promise<void> {
  await api.delete(`${API_ENDPOINTS.CSV_FILES}/${id}`);
}

// Get CSV file versions
export async function getCsvFileVersions(
  csvFileId: number
): Promise<CsvFileVersionsResponse> {
  const response = await api.get(
    `${API_ENDPOINTS.CSV_FILES}/${csvFileId}/versions`
  );

  if (response.data && response.data.data) {
    return { data: response.data.data };
  }

  return { data: response.data || [] };
}

// Update CSV file version status
export async function updateCsvFileVersionStatus(
  csvFileId: number,
  versionId: number,
  status: CsvFileValidationStatus
): Promise<CsvFileVersion> {
  const response = await api.put(
    `${API_ENDPOINTS.CSV_FILES}/${csvFileId}/versions/${versionId}`,
    { status }
  );

  if (response.data && response.data.data) {
    return response.data.data;
  }

  return response.data;
}

// Get CSV file headers and preview data
export async function getCsvFileHeaders(
  studyId: number
): Promise<CsvFileHeadersResponse> {
  const response = await api.get(
    `${API_ENDPOINTS.CSV_FILES}/headers?study_id=${studyId}`
  );

  if (response.data && response.data.data) {
    return { data: response.data.data };
  }

  return { data: response.data || [] };
}

// Get CSV file headers and preview data for selected files only
export async function getSelectedCsvFileHeaders(
  fileIds: number[]
): Promise<CsvFileHeadersResponse> {
  const params = new URLSearchParams();
  params.append('file_id', fileIds.join(','));

  const response = await api.get(
    `${API_ENDPOINTS.CSV_FILES}/headers?${params.toString()}`
  );

  if (response.data && response.data.data) {
    return { data: response.data.data };
  }

  return { data: response.data || [] };
}

// Validate CSV file on client side
export function validateCsvFile(file: File): {
  isValid: boolean;
  error?: string;
} {
  // Check file extension
  const isValidExtension = file.name.toLowerCase().endsWith('.csv');
  if (!isValidExtension) {
    return { isValid: false, error: 'File must have .csv extension' };
  }

  // Check MIME type
  const isValidMimeType = file.type === 'text/csv' || file.type === '';
  if (!isValidMimeType && file.type !== '') {
    return { isValid: false, error: 'File must be a CSV file (text/csv)' };
  }

  return { isValid: true };
}
